import React, {
  Component,
  ReactNode,
  useCallback,
  useEffect,
  useState
} from 'react'
import Button from 'core/components/buttons/Button'
import Icon from 'core/components/icons/Icon'
import { datadogLogs } from '@datadog/browser-logs'
import { isChunkLoadError } from 'utils/Functions'
import { isStale } from 'core/update-check'
import Modal from 'core/components/dialogs/Modal'
import OutOfDateScreen, {
  OUT_OF_DATE_DESCRIPTION,
  OUT_OF_DATE_TITLE
} from 'core/widgets/OutOfDateScreen'

const POLL_INTERVAL_MS = 1000 * 5

interface Props {
  children?: ReactNode
  markOutOfDate?: VoidFunction
  isOutOfDate?: boolean
}

interface State {
  hasError: boolean
  isChunkError: boolean
  id: number
  outOfDateModalDismissed: boolean
}

/**
 * Top-level error boundary that catches all unhandled React errors.
 *
 * Recovery strategy:
 * - **Out-of-date build** (deployed version differs from the running one): shows a
 *   reload modal while the app works, or a reload screen if the app has crashed.
 * - **ChunkLoadError** (failed lazy-loaded chunk): shows hard-refresh instructions
 *   after the network retry logic in `lazyLoadHelper` has already exhausted all attempts.
 * - **All other errors**: logs to Datadog with a timestamped error ID for support
 *   reference and renders a generic error UI.
 */
class ErrorBoundary extends Component<Props, State> {
  public state: State = {
    hasError: false,
    isChunkError: false,
    id: 0,
    outOfDateModalDismissed: false
  }

  /**
   * Synchronously derives error state from the thrown error.
   * Runs during the render phase, so side effects must go in `componentDidCatch`.
   */
  public static getDerivedStateFromError(error: Error): Partial<State> {
    const id = new Date().getTime()
    return { hasError: true, isChunkError: isChunkLoadError(error), id }
  }

  componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
    datadogLogs.logger.error('Error boundary - UI error', {
      error: {
        message: error.message,
        name: error.name,
        stack: error.stack,
        id: this.state.id,
        pathname: window?.location?.pathname,
        search: window?.location?.search
      },
      componentStack: errorInfo.componentStack,
      source: 'error-boundary'
    })

    isStale().then((stale) => {
      if (stale) this.props.markOutOfDate?.()
    })
  }

  private renderContent() {
    if (!this.state.hasError) return this.props.children

    if (this.state.isChunkError && this.props.isOutOfDate) {
      return <OutOfDateScreen />
    }

    const isMac = /mac/i.test(navigator.userAgent)
    return (
      <div className="flex justify-center items-center gap-5 w-screen h-screen flex-col text-center">
        <Icon
          name="exclamation-mark"
          color="text-white"
          className="bg-dark-4 rounded-full p-1"
        />
        <h1 className="text-dark-4 m-0">Server error</h1>
        <div>
          {this.state.isChunkError ? (
            <>
              <p>The page could not load its resources.</p>
              <p>
                Please try a hard refresh (
                {isMac ? 'Cmd+Shift+R' : 'Ctrl+Shift+R'}) and if the problem
                continues{' '}
                <a href="mailto:support@everlance.com" className="underline">
                  contact support
                </a>
              </p>
            </>
          ) : (
            <>
              <p>Oh no! Looks like something broke.</p>
              <p>
                Try refreshing the page and if you need more help{' '}
                <a href="mailto:support@everlance.com" className="underline">
                  contact support
                </a>
              </p>
            </>
          )}
          <p>
            and reference this number{' '}
            <span className="text-dark-4">{this.state.id}</span>.
          </p>
        </div>
        <Button
          variant="primary"
          onClick={() => window.location.assign('/dashboard')}
        >
          Back to home
        </Button>
      </div>
    )
  }

  private onOutOfDateModalClose = () => {
    this.setState({
      outOfDateModalDismissed: true
    })
  }

  private onReloadClick = () => window.location.reload()

  public render() {
    return (
      <>
        <Modal
          show={
            !this.state.outOfDateModalDismissed &&
            !this.state.hasError &&
            this.props.isOutOfDate
          }
          title={OUT_OF_DATE_TITLE}
          onClose={this.onOutOfDateModalClose}
        >
          <div>{OUT_OF_DATE_DESCRIPTION}</div>
          <div className="flex justify-end">
            <Button variant="primary" onClick={this.onReloadClick}>
              Reload
            </Button>
          </div>
        </Modal>
        {this.renderContent()}
      </>
    )
  }
}

/**
 * Wraps ErrorBoundary and watches for new deployments (polling, tab focus and
 * Vite preload errors) to flag the running build as out of date.
 */
export const ConnectedErrorBoundary = ({
  children
}: {
  children: ReactNode
}) => {
  const [isOutOfDate, setIsOutOfDate] = useState(false)

  const markOutOfDate = useCallback(() => {
    setIsOutOfDate(true)
  }, [])

  useEffect(() => {
    const checkVersion = async () => {
      if (await isStale()) markOutOfDate()
    }

    const onVisibilityChange = () => {
      if (document.visibilityState === 'visible') checkVersion()
    }

    checkVersion()
    const interval = setInterval(checkVersion, POLL_INTERVAL_MS)

    window.addEventListener('vite:preloadError', checkVersion)
    document.addEventListener('visibilitychange', onVisibilityChange)

    return () => {
      clearInterval(interval)
      window.removeEventListener('vite:preloadError', checkVersion)
      document.removeEventListener('visibilitychange', onVisibilityChange)
    }
  }, [markOutOfDate])

  return (
    <ErrorBoundary isOutOfDate={isOutOfDate} markOutOfDate={markOutOfDate}>
      {children}
    </ErrorBoundary>
  )
}

export default ErrorBoundary
